import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useApolloClient } from '@apollo/client'
import { signInWithGoogle, signUpWithEmail, auth, getIdToken } from '@/lib/firebase'
import { useAuthStore } from '@/stores/auth'
import { Input } from '@/components/Input'
import { Button } from '@/components/ui/Button'
import { ProgressBar } from '@/components/ProgressBar'
import { CompanyBrandingForm, type CompanyBrandingValue } from '@/components/CompanyBrandingForm'
import { TurnstileWidget, type TurnstileHandle } from '@/components/Turnstile'
import { trackEvent } from '@/hooks/useMixpanel'
import {
  CREATE_TEAM_FOR_NEW_SIGNUP,
  UPDATE_TEAM,
  CREATE_TEAM_BRANDING,
  CREATE_TEAM_BRANDING_ASSET,
  CREATE_TEAM_ADMIN,
} from '@/graphql/mutations/teams'
import { GET_TEAM_ADMINS_BY_PROFILE_ID } from '@/graphql/queries/auth'
import { classNames } from '@/lib/utils'
import { startTrial } from '@/lib/trial'
import { notifyNewAccountCreated } from '@/lib/newAccount'
import { INTERNAL_ADMIN_PROFILE_IDS } from '@/lib/constants'
import { IconSend, IconUsers, IconBrandYoutube, IconMailFilled } from '@/components/icons'

const EXISTING_ACCOUNT_NO_TEAM_MESSAGE =
  'An account with this email already exists. Sign in instead, or ask your brand admin for an invite.'

const SALES_BULLETS = [
  { icon: IconSend, text: 'Launch and track video campaigns with ease' },
  { icon: IconUsers, text: 'Activate unlimited users to capture clips' },
  { icon: IconBrandYoutube, text: 'Download, edit and share your clips' },
]

type Step = 'account' | 'company'
const STEPS: { key: Step; label: string }[] = [
  { key: 'account', label: 'Create Account' },
  { key: 'company', label: 'Company Details' },
]

// Waits for the auth store to actually reflect the real signed-in user before anything that
// depends on it runs — same race LoginPage guards against (Firebase's onAuthStateChanged can
// lag a beat behind the promise this function's callers await).
function waitForAuthenticated(timeoutMs = 3000): Promise<void> {
  if (useAuthStore.getState().isAuthenticated) return Promise.resolve()
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      unsubscribe()
      resolve()
    }, timeoutMs)
    const unsubscribe = useAuthStore.subscribe((state) => {
      if (state.isAuthenticated) {
        clearTimeout(timer)
        unsubscribe()
        resolve()
      }
    })
  })
}

function getErrorMessage(code: string): string {
  switch (code) {
    case 'auth/popup-blocked':
      return 'Popup was blocked. Please allow popups and try again.'
    case 'auth/popup-closed-by-user':
      return 'Sign-in popup was closed. Please try again.'
    case 'auth/network-request-failed':
      return 'Network error. Please check your connection.'
    case 'auth/account-exists-with-different-credential':
      return 'An account already exists with this email using a different sign-in method. Please sign in with your email and password instead.'
    case 'auth/email-already-in-use':
      return 'An account with this email already exists. Please sign in instead.'
    case 'auth/weak-password':
      return 'Password must be at least 6 characters.'
    case 'auth/invalid-email':
      return 'Please enter a valid email address.'
    default:
      return 'An unexpected error occurred. Please try again.'
  }
}

export function SignupPage() {
  const navigate = useNavigate()
  const apolloClient = useApolloClient()
  const setRedeemingInvite = useAuthStore((s) => s.setRedeemingInvite)
  const profile = useAuthStore((s) => s.profile)
  const [step, setStep] = useState<Step>('account')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showEmailForm, setShowEmailForm] = useState(false)
  const [createTeamForNewSignup] = useMutation(CREATE_TEAM_FOR_NEW_SIGNUP, {
    refetchQueries: ['GetTeamAdminsByProfileId'],
    awaitRefetchQueries: true,
  })
  const [updateTeam] = useMutation(UPDATE_TEAM)
  const [startTrialMutation] = useMutation(UPDATE_TEAM)
  const [createTeamAdmin] = useMutation(CREATE_TEAM_ADMIN)

  // Step 1 — Create Account
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  // Cloudflare Turnstile (BAC-1820) — bot protection on front-door signup. Token is single-use,
  // so it's reset after any failed attempt. Passed through to createTeamForNewSignup in
  // afterAccountCreated below, where the backend verifies it server-side (siteverify).
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null)
  const turnstileRef = useRef<TurnstileHandle>(null)

  // Set right after account creation (createTeamForNewSignup, called from afterAccountCreated) —
  // Step 2 updates this same team's name/logo once the user confirms/edits them, rather than
  // creating a second team.
  const [teamId, setTeamId] = useState<string | null>(null)

  // Step 2 — Company Details
  const [companyBranding, setCompanyBranding] = useState<CompanyBrandingValue>({
    companyURL: '',
    companyName: '',
    logoURL: null,
    heroImageURL: null,
  })
  // Drives the "Get Started" button below — stays disabled until a domain lookup has been
  // attempted at least once, matching CompanyBrandingForm's requireLookupFirst field gate.
  const [lookupChecked, setLookupChecked] = useState(false)
  const [createBranding] = useMutation(CREATE_TEAM_BRANDING)
  const [createBrandingAsset] = useMutation(CREATE_TEAM_BRANDING_ASSET)

  const stepIndex = STEPS.findIndex((s) => s.key === step)

  async function afterAccountCreated() {
    trackEvent('Sign Up', { method: 'front_door' })
    await waitForAuthenticated()
    // Suppresses useBootstrap's "not an admin of any team" kickout — this brand-new account has
    // zero team_admins rows until createTeamForNewSignup creates one, a few lines below.
    setRedeemingInvite(true)
    try {
      if (auth.currentUser) await getIdToken(auth.currentUser, true)
      const { data } = await createTeamForNewSignup({ variables: { brand: true, turnstileToken } })
      const result = data?.createTeamForNewSignup
      if (result?.returnCode !== 0) {
        setError(result?.message || 'Failed to create your team. Please try again.')
        return
      }
      setTeamId(result.id ?? null)
      // Same trial-start motions as StartTrialModal, fired automatically right after account
      // creation instead of making a brand-new signup hit that gate on their first visit to the
      // portal. Best-effort — a signup shouldn't be blocked by this failing, so errors here don't
      // stop the flow; profile may not be populated yet at this point (auth just resolved a
      // moment ago), which startTrial already tolerates for its best-effort Zapier webhook.
      if (result.id) {
        startTrial(
          startTrialMutation,
          result.id,
          profile?.id ?? auth.currentUser?.uid,
          profile?.email ?? auth.currentUser?.email ?? undefined,
        ).catch(() => {})
        // Rolla staff get added as internal admins on every new front-door signup so
        // support/CS always has access without a separate invite — best-effort, same as
        // startTrial above, since a signup shouldn't be blocked by this failing.
        for (const profileId of INTERNAL_ADMIN_PROFILE_IDS) {
          createTeamAdmin({
            variables: { newTeamAdmin: { teamId: result.id, profileId, primary: false, role: 'internal' } },
          }).catch(() => {})
        }
      }
      setStep('company')
    } catch {
      setError('Failed to create your team. Please try again.')
    } finally {
      setRedeemingInvite(false)
    }
  }

  async function handleGoogleSignUp() {
    setError(null)
    if (!turnstileToken) {
      setError('Please complete the verification check above.')
      return
    }
    setLoading(true)
    try {
      const { isNewAccount } = await signInWithGoogle(true)
      if (isNewAccount) {
        await afterAccountCreated()
        return
      }
      // This Google identity already had a real account before this sign-in (ADM-703) — running
      // new-brand setup here would create a second top-level brand and move the existing user's
      // profile into it, pulling them out of whatever brand they already belonged to. Instead,
      // route them like a normal login: if they're already an admin of a team, send them into
      // the portal; otherwise tell them to sign in / ask for an invite rather than silently
      // spinning up a brand nobody asked for.
      await waitForAuthenticated()
      const profileId = auth.currentUser?.uid
      const { data } = await apolloClient.query({
        query: GET_TEAM_ADMINS_BY_PROFILE_ID,
        variables: { profileId },
        fetchPolicy: 'network-only',
      })
      const hasTeam = (data?.getTeamAdminsByProfileId?.rows?.length ?? 0) > 0
      if (hasTeam) {
        navigate('/campaigns')
      } else {
        setError(EXISTING_ACCOUNT_NO_TEAM_MESSAGE)
      }
    } catch (err: unknown) {
      const code = (err as { code?: string }).code ?? ''
      setError(getErrorMessage(code))
      turnstileRef.current?.reset()
      setTurnstileToken(null)
    } finally {
      setLoading(false)
    }
  }

  async function handleEmailSignUp(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (password !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }
    if (!turnstileToken) {
      setError('Please complete the verification check above.')
      return
    }
    setLoading(true)
    try {
      await signUpWithEmail(email, password)
      await afterAccountCreated()
    } catch (err: unknown) {
      const code = (err as { code?: string }).code ?? ''
      setError(getErrorMessage(code))
      turnstileRef.current?.reset()
      setTurnstileToken(null)
    } finally {
      setLoading(false)
    }
  }

  async function handleCompanyContinue(e: React.FormEvent) {
    e.preventDefault()
    const { companyName, companyURL, logoURL, heroImageURL } = companyBranding
    if (!companyName.trim()) {
      setError('Enter a company name to continue.')
      return
    }
    setError(null)
    // The team already exists (created right after step 1) — this applies whatever the user
    // confirmed/edited here, rather than creating a second team.
    if (teamId) {
      setLoading(true)
      try {
        const { data } = await updateTeam({
          variables: {
            changedTeam: {
              id: teamId,
              name: companyName,
              teamLogoURL: logoURL,
              websiteURL: companyURL.trim() || undefined,
            },
          },
        })
        if (data?.updateTeam?.returnCode !== 0) {
          setError(data?.updateTeam?.message || 'Failed to save company details. Please try again.')
          return
        }

        // Mirrors BrandingPage's ensureBrandingExists — every new signup team gets its own
        // teamBranding + teamBrandingAsset row, seeded with whatever Brandfetch found (or
        // whatever the user swapped in), so the branded web app has something to show
        // immediately instead of an admin having to visit the Branding page first.
        // teamBrandings.teamId (the reverse FK) is deprecated — teams.teamBrandingId, set via the
        // checked updateTeam call below, is the current, canonical link.
        if (logoURL || heroImageURL) {
          const { data: brandingData } = await createBranding({
            variables: { newTeamBranding: { name: companyName, signInOptions: ['email', 'google'] } },
          })
          const brandingId = brandingData?.createTeamBranding?.id
          if (brandingId) {
            const [linkResult] = await Promise.all([
              updateTeam({ variables: { changedTeam: { id: teamId, teamBrandingId: brandingId } } }),
              createBrandingAsset({
                variables: {
                  newTeamBrandingAsset: {
                    teamBrandingId: brandingId,
                    logoNoPoweredByLargeURL: logoURL ?? undefined,
                    logoDarkPoweredByLargeURL: logoURL ?? undefined,
                    logoLightPoweredByLargeURL: logoURL ?? undefined,
                    heroImageURL: heroImageURL ?? undefined,
                  },
                },
              }),
            ])
            if (linkResult.data?.updateTeam?.returnCode !== 0) {
              throw new Error(linkResult.data?.updateTeam?.message || 'Failed to link branding to team')
            }
          }
        }

        const profileName =
          [profile?.firstName, profile?.lastName].filter(Boolean).join(' ') || auth.currentUser?.displayName || ''
        notifyNewAccountCreated({
          type: 'selfService',
          topLevelTeamId: teamId,
          topLevelTeamName: companyName,
          logoURL,
          profile: {
            userId: profile?.id ?? auth.currentUser?.uid ?? '',
            name: profileName,
            email: profile?.email ?? auth.currentUser?.email ?? '',
          },
        })
      } catch {
        setError('Failed to save company details. Please try again.')
        return
      } finally {
        setLoading(false)
      }
    }
    navigate('/campaigns')
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-gray-50 px-4 py-10">
      <div
        className="pointer-events-none absolute top-1/2 left-1/2 h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-40 blur-[120px]"
        style={{ backgroundColor: '#e74f31' }}
        aria-hidden="true"
      />

      <div className="relative w-full max-w-lg rounded-2xl border border-gray-200 bg-white shadow-xl">
        <div className="flex flex-col items-center px-8 pt-8 text-center sm:px-10">
          <img src="/rolla-logo.png" alt="Rolla" className="h-7 w-auto" />
          <p className="mt-2 text-sm text-gray-500">Admin Portal</p>
        </div>

        {/* Step indicators — same visual pattern as ClipSubmissionModal's "Submit for Edit" stepper */}
        <div className="mt-6 flex items-start gap-3 border-b border-gray-200 px-8 pb-4 sm:px-10">
          {STEPS.map((s, i) => (
            <ProgressBar key={s.key} value={i <= stepIndex ? 100 : 0} className="h-1.5 w-full" />
          ))}
        </div>

        <div className="px-8 py-6 sm:px-10">
          {error && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {step === 'account' && (
            <>
              <h2 className="text-center text-xl font-semibold text-gray-900">Create your account</h2>
              <p className="mt-1 text-center text-sm font-medium text-primary-600">
                Start your 30 day free trial — No CC required
              </p>

              <div className="mt-5 space-y-2.5 rounded-lg bg-gray-50 px-4 py-3.5">
                {SALES_BULLETS.map((bullet) => (
                  <div key={bullet.text} className="flex items-center gap-2.5">
                    <bullet.icon className="h-4 w-4 flex-shrink-0 text-primary-600" />
                    <p className="text-sm text-gray-700">{bullet.text}</p>
                  </div>
                ))}
              </div>

              <div className="mt-5 flex justify-center">
                <TurnstileWidget ref={turnstileRef} action="signup" onToken={setTurnstileToken} />
              </div>

              <button
                type="button"
                onClick={handleGoogleSignUp}
                disabled={loading}
                className="mt-5 flex w-full items-center justify-center gap-3 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition hover:shadow disabled:opacity-50"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24">
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                </svg>
                Continue with Google
              </button>

              {!showEmailForm && (
                <button
                  type="button"
                  onClick={() => setShowEmailForm(true)}
                  disabled={loading}
                  className="mt-3 flex w-full items-center justify-center gap-3 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 shadow-sm transition hover:shadow disabled:opacity-50"
                >
                  <IconMailFilled className="h-5 w-5 text-gray-500" />
                  Continue with Email
                </button>
              )}

              {/* grid-rows 0fr -> 1fr (not a conditional unmount) is what makes this animatable at
                  all — height: auto can't be transitioned, and the content's real height isn't
                  known until it renders, so a plain max-height guess would either clip it or
                  leave a jump. A 1fr grid track sized to content, animated from a 0fr track,
                  transitions smoothly to that real height with no measurement needed. */}
              <div
                className={classNames(
                  'grid transition-[grid-template-rows] duration-300 ease-in-out',
                  showEmailForm ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
                )}
              >
                <div className="overflow-hidden">
                  <div className="my-6 flex items-center gap-3">
                    <div className="h-px flex-1 bg-gray-200" />
                    <span className="text-xs text-gray-400">or</span>
                    <div className="h-px flex-1 bg-gray-200" />
                  </div>

                  <form onSubmit={handleEmailSignUp} className="space-y-4">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                        Email
                      </label>
                      <Input
                        id="email"
                        type="email"
                        required={showEmailForm}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                        Password
                      </label>
                      <Input
                        id="password"
                        type="password"
                        required={showEmailForm}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Create a password"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                        Confirm Password
                      </label>
                      <Input
                        id="confirmPassword"
                        type="password"
                        required={showEmailForm}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        placeholder="Re-enter your password"
                        className="mt-1"
                      />
                    </div>
                    <Button type="submit" variant="primary" disabled={loading} className="w-full justify-center">
                      {loading ? 'Please wait...' : 'Create Account'}
                    </Button>
                  </form>
                </div>
              </div>

              <p className="mt-6 text-center text-sm text-gray-500">
                Already have an account?{' '}
                <a href="/login" className="text-primary-600 hover:text-primary-700">
                  Sign in
                </a>
              </p>
            </>
          )}

          {step === 'company' && (
            <form onSubmit={handleCompanyContinue}>
              <h2 className="mb-2 text-center text-xl font-semibold text-gray-900">Tell us about your company</h2>
              <p className="mb-6 text-center text-sm text-gray-500">
                We&apos;ll try to pull your name and logo automatically.
              </p>

              <CompanyBrandingForm
                value={companyBranding}
                onChange={setCompanyBranding}
                uploadNamespace={teamId ?? 'signup'}
                onError={(message) => setError(message || null)}
                requireLookupFirst
                onLookupCheckedChange={setLookupChecked}
              />

              <div className="mt-6 flex justify-end">
                <Button type="submit" variant="primary" disabled={loading || !lookupChecked}>
                  {loading ? 'Saving...' : 'Get Started'}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
